'use client'
import React, { useState } from 'react'
import type {
  Image as SanityImage,
  LocaleString,
} from '@/.generated/sanityTypes/graphql'
import { withNewLines } from '@/lib/newline'
import ScrollDownButton from './ScrollDownButton'
import { useLocale } from 'next-intl'
import type { Locale } from '@/i18n'
import { usePathname } from 'next/navigation'
import titleImageDE from '../../../../../public/2022/title-DE.png'
import titleImageIT from '../../../../../public/2022/title-IT.png'
import titleImageEN from '../../../../../public/2022/title-EN.png'
import titleImageFR from '../../../../../public/2022/title-FR.png'
import { getImageObjectPosition } from '@/lib/image'
import { cx } from '@monorepo/helper/cx'
import Image from '@/components/Image'

type TitleBlockProps = {
  title: LocaleString
  subtitle: LocaleString
  image: SanityImage
  imageCredit: LocaleString
  imageDescription: LocaleString
}

const TitleBlock = ({
  title,
  subtitle,
  image,
  imageCredit,
  imageDescription,
}: TitleBlockProps) => {
  const pathname = usePathname()
  const [is2022] = useState(pathname?.includes('2022'))
  const locale = useLocale() as Locale
  let img = image?.asset

  // for 2022 we use a different image and style
  if (is2022) {
    let localeImage = titleImageDE
    if (locale == 'it') localeImage = titleImageIT
    if (locale == 'en') localeImage = titleImageEN
    if (locale == 'fr') localeImage = titleImageFR

    img = {
      url: localeImage.src,
      metadata: {
        lqip: localeImage.blurDataURL,
        dimensions: {
          width: localeImage.width,
          height: localeImage.height,
        },
      },
    }
  }

  return (
    <div
      className={cx(
        'relative flex',
        !is2022 && 'h-[calc(100vh-65px)] max-h-[960px]',
      )}
    >
      {img?.url && (
        // eslint-disable-next-line jsx-a11y/alt-text
        <Image
          image={img}
          fill={!is2022}
          height={is2022 ? img.metadata?.dimensions?.height || 0 : undefined}
          width={is2022 ? img.metadata?.dimensions?.width || 0 : undefined}
          className={cx(
            'inset-0 h-full',
            is2022 ? 'object-contain' : 'object-cover',
          )}
          style={{
            objectPosition: getImageObjectPosition(image),
          }}
          sizes="100vw"
          priority
        />
      )}
      {!is2022 && (
        <div className="bg-black/8 relative flex w-full py-10">
          <div className="mx-auto flex max-w-screen-lg flex-col justify-center px-5 md:px-0 2xl:max-w-screen-xl">
            <div className="text-center">
              <h1 className="sm:md-8 xs:text-xl xs:mb-2 text-yellow mb-1 text-lg uppercase leading-8 tracking-wider sm:text-2xl md:text-3xl xl:text-4xl">
                {withNewLines(subtitle?.[locale])}
              </h1>
              <p className="xs:text-4xl text-3xl font-medium text-white sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-9xl">
                {withNewLines(title?.[locale])}
              </p>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 z-10 justify-center px-5 py-5 sm:px-10 sm:py-10">
            <p className="text-xs font-bold text-white sm:text-base md:text-xl">
              {imageDescription?.[locale]}
            </p>
            <p className="text-xs text-white sm:text-base md:text-xl">
              {imageCredit?.[locale]}
            </p>
          </div>
        </div>
      )}
      <ScrollDownButton />
    </div>
  )
}

export default TitleBlock
